/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useMemo } from 'react';
import useExternalCollection from '@broadleaf/admin-components/dist/form/hooks/useExternalCollection';
import { mustHaveMetadataProps } from '@broadleaf/admin-components/dist/form/utils/invariantHelpers';
import {
  getAttribute,
  getCustomCssClasses
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { IFormik } from '@broadleaf/admin-components/dist/types/form';
import type { IMetadata } from '@broadleaf/admin-components/dist/types/metadata';
import { get, omit } from 'lodash';
import { AuroraActionListGrid } from '../ActionListGrid/AuroraActionListGrid';

export const AuroraExternalGridCollection: React.FC<
  ExternalGridCollectionProps
> = props => {
  mustHaveMetadataProps(props.metadata, ['actions', 'components', 'endpoints']);
  const { readOnly, ...rest } = props;
  const { getActionListGridProps } = useExternalCollection({
    ...rest,
    disabled: readOnly
  });
  const actionListGridProps = getActionListGridProps();
  const border = getAttribute(props.metadata, 'border', true);
  // Overridden to include context params
  const renderColumn = useMemo(() => {
    const renderColumn = get(actionListGridProps, 'renderColumn');
    if (!renderColumn) {
      return renderColumn;
    }
    return (args: any) => {
      const contextParams = get(
        actionListGridProps,
        'actionProps.contextParams',
        {}
      );
      return renderColumn({
        ...args,
        contextParams
      });
    };
  }, [actionListGridProps]);
  return (
    <div className="form-group ExternalGridCollection">
      <AuroraActionListGrid
        border={border}
        {...omit(actionListGridProps, 'renderColumn')}
        augmentationKey={props.metadata.augmentationKey}
        renderColumn={renderColumn}
        customCssClasses={getCustomCssClasses(props.metadata)}
      />
    </div>
  );
};

export interface ExternalGridCollectionProps {
  /** The formik object of the parent form. */
  formik: IFormik;
  /** The metadata for this component. */
  metadata: IMetadata;
  /**
   * Whether the collection is disabled
   * @deprecated To avoid inheriting from a parent component
   */
  disabled?: boolean;
  /**
   * To allow disabling due to access checks (permissions)
   */
  readOnly?: boolean;
}

export default AuroraExternalGridCollection;
